import React, { useCallback } from 'react';

import { HangerPrice } from '@paragon/contract-library';
import { getConnectingHardwareName } from '@paragon/material-lists';

import { editHangerPrices } from '../../actions/hangerPriceActions';
import { formatDateTime } from '../formattingUtilities';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ColumnData, PriceGrid } from '../price-grid/PriceGrid';
import { parsePriceEditValue } from '../price-grid/priceGridUtilities';
import { InsertHangerPrice } from './InsertHangerPrice';

const columns: readonly ColumnData<HangerPrice>[] = [
  {
    key: 'hanger',
    title: 'Type',
    width: 115,
    getCellValue: (row) => getConnectingHardwareName(row.hanger),
    filterType: 'select',
    filterLabel: 'All types',
  },
  {
    key: 'price',
    title: 'Price per Unit',
    width: 170,
    getCellValue: (row) => `$${row.price.toFixed(2)}`,
    getUpdatedRowWithEdit: (row, newValue) => ({ ...row, price: parsePriceEditValue(newValue) }),
    getEditValue: (row) => row.price.toFixed(2),
    editStartAdornment: '$',
    validateEditValue: (newValue) => {
      const parsedValue = Number(newValue);
      if (newValue.trim() === '' || !Number.isFinite(parsedValue) || parsedValue < 0) {
        return 'Enter a non-negative price.';
      }
      return undefined;
    },
    sortByMapping: (row) => row.price,
    filterType: 'range',
    rangeFilterLabels: { min: 'Min price', max: 'Max price' },
  },
  {
    key: 'priceDateTime',
    title: 'Last Updated',
    width: 200,
    getCellValue: (row) => formatDateTime(row.priceDateTime),
    sortByMapping: (row) => Date.parse(row.priceDateTime),
  },
  {
    key: 'notes',
    title: 'Notes',
    width: 200,
    getCellValue: (row) => row.notes ?? '',
    getUpdatedRowWithEdit: (row, newValue) => ({ ...row, notes: newValue === '' ? null : newValue }),
    filterType: 'text',
    filterLabel: 'Search notes',
  },
];

export function HangerPriceGrid() {
  const dispatch = useAppDispatch();
  const hangerPrices = useAppSelector((state) => state.hangerPrices);

  const commitRowEdits = useCallback(
    (editedRows: readonly HangerPrice[]) => {
      return dispatch(editHangerPrices(editedRows));
    },
    [dispatch],
  );

  return (
    <PriceGrid
      rows={Object.values(hangerPrices)}
      columns={columns}
      commitRowEdits={commitRowEdits}
      insertBar={(onClose, onHeightChange) => <InsertHangerPrice onClose={onClose} onHeightChange={onHeightChange} />}
      defaultSorting={[{ columnKey: 'priceDateTime', direction: 'desc' }]}
    />
  );
}
